/* eslint-disable */
import { inject, Injectable } from '@angular/core';
import {
  arrayUnion,
  collection,
  collectionData,
  doc,
  docSnapshots,
  Firestore,
  onSnapshot,
  setDoc
} from '@angular/fire/firestore';
import { firstValueFrom, map, Observable, Subscriber } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { FirebaseFirestore } from '@capacitor-firebase/firestore';

@Injectable({ providedIn: 'root' })
export class FirestoreService {
  private readonly firestore: Firestore = inject(Firestore);

  documentSnapshot<T>(collectionPath: string, documentId: string): Observable<T> {
    if (Capacitor.isNativePlatform()) {
      return this.documentSnapshotNative<T>(collectionPath, documentId);
    }
    return this.documentSnapshotWeb<T>(collectionPath, documentId);
  }

  collectionData<T>(collectionPath: string, options?: { idField?: string }): Observable<T[]> {
    if (Capacitor.isNativePlatform()) {
      return this.collectionDataNative<T>(collectionPath, options);
    }
    return this.collectionDataWeb<T>(collectionPath, options);
  }

  subcollectionSnapshot<T>(
    parentCollectionPath: string,
    parentDocumentId: string,
    subcollectionPath: string
  ): Observable<T[]> {
    if (Capacitor.isNativePlatform()) {
      return this.subcollectionSnapshotNative<T>(parentCollectionPath, parentDocumentId, subcollectionPath);
    }
    return this.subcollectionSnapshotWeb<T>(parentCollectionPath, parentDocumentId, subcollectionPath);
  }

  appendToArray<T>(collectionPath: string, documentId: string, field: string, value: T): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return this.appendToArrayNative(collectionPath, documentId, field, value);
    }
    return this.appendToArrayWeb(collectionPath, documentId, field, value);
  }

  updateDocument(collectionPath: string, documentId: string, data: Record<string, unknown>): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      return this.mergeSetNative(collectionPath, documentId, data);
    }
    return this.mergeSetWeb(collectionPath, documentId, data);
  }

  private documentSnapshotNative<T>(collectionPath: string, documentId: string): Observable<T> {
    return new Observable<T>((observer: Subscriber<T>) => {
      const listenerPromise = FirebaseFirestore.addDocumentSnapshotListener(
        {
          reference: `${collectionPath}/${documentId}`
        },
        (event, error) => {
          if (error) {
            observer.error(error);
            return;
          }
          if (event?.snapshot) {
            observer.next(event.snapshot.data as T);
          }
        }
      );

      return () => {
        listenerPromise.then((callbackId) => {
          FirebaseFirestore.removeSnapshotListener({ callbackId });
        });
      };
    });
  }

  private documentSnapshotWeb<T>(collectionPath: string, documentId: string): Observable<T> {
    return new Observable<T>((observer) => {
      const documentRef = doc(this.firestore, collectionPath, documentId);

      const subscription = docSnapshots(documentRef)
        .pipe(map((snapshot) => snapshot.data() as T))
        .subscribe({
          next: (value) => observer.next(value),
          error: (error) => observer.error(error),
          complete: () => observer.complete()
        });

      return () => subscription.unsubscribe();
    });
  }

  private collectionDataNative<T>(collectionPath: string, options?: { idField?: string }): Observable<T[]> {
    return new Observable<T[]>((observer) => {
      const listenerPromise = FirebaseFirestore.addCollectionSnapshotListener(
        {
          reference: collectionPath
        },
        (event, error) => {
          if (error) {
            observer.error(error);
            return;
          }
          if (event?.snapshots) {
            const items = event.snapshots.map((snapshot) => {
              const data = snapshot.data as any;
              if (options?.idField) {
                data[options.idField] = snapshot.id;
              }
              return data as T;
            });
            observer.next(items);
          }
        }
      );

      return () => {
        listenerPromise.then((callbackId) => {
          FirebaseFirestore.removeSnapshotListener({ callbackId });
        });
      };
    });
  }

  private collectionDataWeb<T>(collectionPath: string, options?: { idField?: string }): Observable<T[]> {
    return new Observable<T[]>((observer) => {
      const collectionRef = collection(this.firestore, collectionPath);

      const subscription = collectionData(collectionRef, options).subscribe({
        next: (value) => observer.next(value as T[]),
        error: (error) => observer.error(error),
        complete: () => observer.complete()
      });

      return () => subscription.unsubscribe();
    });
  }

  private subcollectionSnapshotNative<T>(
    parentCollectionPath: string,
    parentDocumentId: string,
    subcollectionPath: string
  ): Observable<T[]> {
    return new Observable<T[]>((observer) => {
      const listenerPromise = FirebaseFirestore.addCollectionSnapshotListener(
        {
          reference: `${parentCollectionPath}/${parentDocumentId}/${subcollectionPath}`
        },
        (event, error) => {
          if (error) {
            observer.error(error);
            return;
          }
          if (event?.snapshots) {
            const items = event.snapshots.map((snapshot) => ({
              id: snapshot.id,
              ...(snapshot.data as object)
            })) as T[];
            observer.next(items);
          }
        }
      );

      return () => {
        listenerPromise.then((callbackId) => {
          FirebaseFirestore.removeSnapshotListener({ callbackId });
        });
      };
    });
  }

  private subcollectionSnapshotWeb<T>(
    parentCollectionPath: string,
    parentDocumentId: string,
    subcollectionPath: string
  ): Observable<T[]> {
    return new Observable<T[]>((observer) => {
      const parentDocumentRef = doc(this.firestore, parentCollectionPath, parentDocumentId);
      const subcollectionRef = collection(parentDocumentRef, subcollectionPath);

      const unsubscribe = onSnapshot(
        subcollectionRef,
        (snapshot) => {
          const items = snapshot.docs.map((documentSnapshot) => ({
            id: documentSnapshot.id,
            ...documentSnapshot.data()
          })) as T[];

          observer.next(items);
        },
        (error) => observer.error(error)
      );

      return () => unsubscribe();
    });
  }

  private mergeSetNative(
    collectionPath: string,
    documentId: string,
    partialData: Record<string, unknown>
  ): Promise<void> {
    return FirebaseFirestore.setDocument({
      reference: `${collectionPath}/${documentId}`,
      data: partialData,
      merge: true
    });
  }

  private mergeSetWeb(collectionPath: string, documentId: string, partialData: Record<string, unknown>): Promise<void> {
    const documentRef = doc(this.firestore, collectionPath, documentId);
    return setDoc(documentRef, partialData, { merge: true });
  }

  private async appendToArrayNative<T>(
    collectionPath: string,
    documentId: string,
    field: string,
    value: T
  ): Promise<void> {
    const currentData = await firstValueFrom(
      this.documentSnapshotNative<Record<string, unknown>>(collectionPath, documentId)
    );

    let currentArray: T[] = [];
    const fieldValue = currentData?.[field];

    if (Array.isArray(fieldValue)) {
      currentArray = fieldValue;
    } else if (fieldValue && typeof fieldValue === 'object' && 'vc' in fieldValue) {
      // Handle corrupted arrayUnion format from previous bug
      currentArray = (fieldValue as { vc: T[] }).vc;
    }

    if (!currentArray.includes(value)) {
      currentArray.push(value);
    }

    return this.mergeSetNative(collectionPath, documentId, { [field]: currentArray });
  }

  private appendToArrayWeb<T>(collectionPath: string, documentId: string, field: string, value: T): Promise<void> {
    const documentRef = doc(this.firestore, collectionPath, documentId);
    return setDoc(documentRef, { [field]: arrayUnion(value) }, { merge: true });
  }
}
